import React, { useEffect, useState, useCallback, useRef } from 'react';
import UpgradePrompt from './common/UpgradePrompt';
import { getAuth } from 'firebase/auth';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Box,
  Tooltip,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Popover,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DOMPurify from 'dompurify';
import config from '../config';
import './Detail.css';
import { Helmet } from 'react-helmet-async';
import { logAnalyticsEvent } from '../utils/analytics';
import usePageReadTracking from '../hooks/usePageReadTracking';
import ArticlePdfView from './article/ArticlePdfView';
import DeepAnalysisSection from './article/DeepAnalysisSection';
import DeepDiveRateLimited from './article/DeepDiveRateLimited';
import { selectDeepDiveView } from '../utils/deepDiveView';
import ArticleHero from './article/ArticleHero';
import ResearchStackCta from './article/ResearchStackCta';
import ProFeatureBadge from './common/ProFeatureBadge';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { ScienceIcon } from '../icons';
import { color, r } from '../theme/tokens';

const ErrorDialog = ({ open, onClose, message }) => {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', top: r(20), left: '50%', transform: 'translateX(-50%)', padding: r(20), background: 'red', color: 'white', zIndex: 1000 }}>
      <p>{message}</p>
      <button onClick={onClose}>Close</button>
    </div>
  );
};

const encodePathSegment = (value) => encodeURIComponent(String(value || '').trim());
const getStudyStrengthScore = (studyStrength) => {
  try {
    const parsed = typeof studyStrength === 'string'
      ? JSON.parse(studyStrength)
      : studyStrength;
    return Number(parsed?.score || 0);
  } catch (_) {
    return 0;
  }
};

const ArticleDetail = ({ conditions, supplements }) => {
  
  const { articleId, supplementKey, conditionKey } = useParams();
  
  // Debug mode state - defaults to OFF
  const [debugModeEnabled, setDebugModeEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('articlesDebugMode');
    return saved === 'true';
  });
  
  // Provider state - null means use production default
  const [provider, setProvider] = useState(() => {
    if (typeof window === 'undefined' || !debugModeEnabled) return null;
    const p = new URLSearchParams(window.location.search).get('provider');
    return p === 'gemini' ? 'gemini' : p === 'openai' ? 'openai' : null;
  });
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' | 'analysis'
  const { hasAccess } = useFeatureAccess();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIsRateLimited(false);
    try {
      const queryParams = new URLSearchParams({
        mode: 'single',
        article_ref: articleId
      });

      // Only append provider if debug mode is enabled AND provider is set
      if (process.env.REACT_APP_ENVIRONMENT !== 'production' && debugModeEnabled && provider) {
        queryParams.append('provider', provider);
      }

      // Attach Firebase ID token if user is logged in — edge function uses it to
      // gate pdf_insights content. Unauthenticated visits still work; they just
      // receive has_pdf_insights: true without the content.
      const headers = {};
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (_) { /* non-fatal — proceed without token */ }

      const response = await fetch(
        `${config.EDGE_FUNCTION_URL}/getResearchArticles?${queryParams}`,
        { headers }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          logAnalyticsEvent('research_rate_limit_reached', { article_ref: articleId });
          setIsRateLimited(true);
          return;
        }
        if (response.status === 403) {
          // Fires when the edge function CIDR-blocks the request.
          // Correlates with edge_scraper_blocked server-side event via article_ref.
          logAnalyticsEvent('article_load_blocked', { article_ref: articleId, status: 403 });
        }
        throw new Error(errorData.message || 'Unable to load article. Please try again later.');
      }

      const result = await response.json();
      if (result.data && ( (Array.isArray(result.data) && result.data.length > 0) || typeof result.data === 'object')) {
         setArticle(Array.isArray(result.data) ? result.data[0] : result.data);
      } else {
        throw new Error('Article not found');
      }

    } catch (error) {
      console.error("Fetch Article Error:", error);
      
      let errorMessage = 'Unable to load this article. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'You don\'t have permission to view this article. Please sign in.';
      } else if (error.code === 'not-found') {
        errorMessage = 'This article could not be found. It may have been removed.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Article service temporarily unavailable. Please try again in a moment.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [articleId, provider, debugModeEnabled]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchArticle();
      } catch (error) {
        setError(error);
      }
    };
    loadData();
    window.scrollTo(0, 0);
  }, [fetchArticle]);

  // Keep URL param in sync with current provider (dev/staging only)
  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== 'production' && debugModeEnabled && provider) {
      const sp = new URLSearchParams(window.location.search);
      sp.set('provider', provider);
      const newUrl = `${window.location.pathname}?${sp.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [provider, debugModeEnabled]);

  // Log view_item event when article data is loaded
  useEffect(() => {
    if (article && !loading && !error) {
      logAnalyticsEvent('view_item', {
        item_id: articleId,
        item_name: article.ai_title_simplified || article.title || 'Unknown Article',
        item_category: 'research_article', 
        parent_item_id: supplementKey || conditionKey,
        parent_item_category: supplementKey ? 'supplements' : 'conditions'
      });
    }
  }, [article, loading, error, articleId, supplementKey, conditionKey]);

  // ── Engagement tracking (STACK-479) ──────────────────────────────────────
  // Hoisted out of the JSX below so the tracking gates read the same branch the
  // renderer does; the IIFE in the tab content reuses this value.
  const deepDiveView = selectDeepDiveView({
    viewMode,
    hasPdfInsights: !!article?.has_pdf_insights,
    hasAccess: hasAccess('pdf_insights'),
    pdfInsightsPresent: !!article?.pdf_insights,
  });
  const articleTitle = article?.ai_title_simplified || article?.title;
  const articleReadable = !!article && !loading && !error && !isRateLimited;

  const summaryRef = useRef(null);
  const deepDiveRef = useRef(null);

  usePageReadTracking({
    surface: 'research_article',
    contentId: articleId,
    contentTitle: articleTitle,
    contentRef: summaryRef,
    enabled: articleReadable && deepDiveView === 'summary',
  });

  // 'content' is the only branch that puts real deep-dive text in front of a
  // member — 'locked' is the blurred upsell and 'limited' is the cap message,
  // and counting either as a read would poison the Pro-value signal.
  usePageReadTracking({
    surface: 'research_deep_dive',
    contentId: articleId,
    contentTitle: articleTitle,
    contentRef: deepDiveRef,
    enabled: articleReadable && deepDiveView === 'content',
  });

  const generateStructuredData = () => {
    if (!article) return null;
    const headline = article.ai_title_simplified || article.title;
    if (!headline) return null;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ScholarlyArticle",
      "headline": `${headline} | myStack Research`,
      "name": headline,
      "datePublished": article.date_published || new Date().toISOString(),
      "publisher": {
        "@type": "Organization",
        "name": article.journal_title || "myStack"
      },
      "description": generateDescription(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${window.location.origin}/${supplementKey ? 'supplements' : 'conditions'}/${supplementKey || conditionKey}/research/${articleId}`
      }
    };

    if (article.first_author_name) {
      structuredData.author = [{
        "@type": "Person",
        "name": article.first_author_name,
        ...(article.first_author_affiliation && {
          "affiliation": {
            "@type": "Organization",
            "name": article.first_author_affiliation
          }
        })
      }];
    }

    if (Array.isArray(article.ai_effects) && article.ai_effects.length > 0) {
      structuredData.keywords = article.ai_effects.join(", ");
    }

    if (article.ai_subject_condition) {
      structuredData.about = [{
        "@type": "MedicalCondition",
        "name": article.ai_subject_condition
      }];
    }

    return structuredData;
  };

  const generateDescription = () => {
    if (!article) return '';
    const description = article.ai_abstract_summary || article.abstract;
    if (!description) return '';
    const cleanDescription = typeof description === 'string' ? description.replace(/<[^>]*>/g, '') : '';
    let truncated = cleanDescription.substring(0, 155);
    const lastPeriod = truncated.lastIndexOf('.');
    if (lastPeriod > 120 && lastPeriod < truncated.length - 1) {
      truncated = truncated.substring(0, lastPeriod + 1);
    } else if (truncated.length === 155 && truncated.charAt(154) !== '.') {
      truncated = truncated.trim() + '...';
    }
    return truncated;
  };

  const handleShareClick = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!successful) throw new Error('Copy command was unsuccessful');
      }
      
      setSnackbarMessage('Link copied to clipboard!');
      setSnackbarOpen(true);
      handleClosePopover();
    } catch (err) {
      console.error('Failed to copy link:', err);
      setSnackbarMessage('Failed to copy link.');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const formatAbstractText = (text) => {
    if (!text) return '';
    
    // Split by double line breaks first (for paragraph separation)
    const paragraphs = text.split(/\n\s*\n/);
    
    // Process each paragraph, also handling single line breaks
    return paragraphs
      .map(para => para.trim())
      .filter(para => para.length > 0)
      .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isRateLimited) {
    return (
      <UpgradePrompt
        feature="Research Articles"
        description="You've reached your weekly limit of free article views."
        size="medium"
        source="rate_limit"
      />
    );
  }

  if (error) {
    return <ErrorDialog open={true} onClose={() => setError(null)} message={error} />;
  }

  if (!article) {
    return <Typography>Article not found.</Typography>;
  }

  const renderChips = (items = []) => {
    if (!Array.isArray(items)) return null;
    return items.map((item, index) => (
      <Chip
        key={index}
        label={item}
        size="small"
        sx={{
          mr: '0.375rem',
          mb: '0.375rem',
          fontSize: '0.78rem',
          fontWeight: 500,
          height: '1.75rem',
          backgroundColor: 'rgba(46,125,50,0.06)',
          color: 'primary.main',
          border: '0.0625rem solid rgba(46,125,50,0.2)',
          borderRadius: '0.5rem',
          '& .MuiChip-label': { px: '0.625rem' },
        }}
      />
    ));
  };

  const openPopover = Boolean(popoverAnchorEl);
  const popoverId = openPopover ? 'share-popover' : undefined;

  const metaDescription = generateDescription();
  const structuredData = generateStructuredData();
  const pageTitle = `${article.ai_title_simplified || article.title || 'Research Article'} | myStack`;
  const collection = supplementKey ? 'supplements' : 'conditions';
  const collectionKey = encodePathSegment(supplementKey || conditionKey);
  const canonicalUrl = `https://my-stack.ai/${collection}/${collectionKey}/research/${encodePathSegment(articleId)}`;
  const studyStrengthScore = getStudyStrengthScore(article.ai_study_strength);
  const shouldNoindex = studyStrengthScore < 5;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {shouldNoindex && <meta name="robots" content="noindex,follow" />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
      </Helmet>

      <Box sx={{ 
        px: { xs: '1rem', sm: '1.5rem', md: '2rem' },
        maxWidth: 900, 
        mx: 'auto', 
        mt: { xs: 2, sm: 3, md: 4 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: { xs: 2.5, sm: 3 },
          pb: 2,
          borderBottom: '0.0625rem solid',
          borderColor: 'divider'
        }}>
          <Button 
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />} 
            onClick={() => {
              const basePath = supplementKey 
                ? `/supplements/${supplementKey}` 
                : `/conditions/${conditionKey}`;
              navigate(`${basePath}/research`); 
            }}
            sx={{ 
              color: 'primary.main',
              textTransform: 'none',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontWeight: 500,
              py: 0.75, 
              px: '0.75rem',
              borderRadius: '0.5rem',
              '&:hover': {
                backgroundColor: 'primary.light',
                opacity: 0.8
              }
            }}
          >
            View All Research 
          </Button>
          {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Debug Mode Switch */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(0, 0, 0, 0.6)', 
                    fontSize: { xs: r(11), sm: r(13) },
                    fontWeight: 500
                  }}
                >
                  Debug:
                </Typography>
                <input
                  type="checkbox"
                  checked={debugModeEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setDebugModeEnabled(enabled);
                    try {
                      localStorage.setItem('articlesDebugMode', enabled.toString());
                      if (!enabled) {
                        // Clear provider when disabling debug
                        localStorage.removeItem('articlesProvider');
                        setProvider(null);
                      }
                    } catch {}
                  }}
                  style={{
                    width: r(16),
                    height: r(16),
                    cursor: 'pointer',
                  }}
                />
              </Box>

              {/* Provider Dropdown - only shown when debug enabled */}
              {debugModeEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: { xs: r(11), sm: r(13) } }}
                  >
                    Provider:
                  </Typography>
                  <Box
                    component="select"
                    value={provider || 'default'}
                    onChange={(e) => {
                      const value = e.target.value;
                      const newProvider = value === 'default' ? null : value;
                      setProvider(newProvider);
                      try {
                        if (newProvider) {
                          localStorage.setItem('articlesProvider', newProvider);
                        } else {
                          localStorage.removeItem('articlesProvider');
                        }
                      } catch {}
                    }}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: r(6),
                      border: `${r(1)} solid rgba(0,0,0,0.2)`,
                      backgroundColor: color.paper,
                      fontSize: { xs: r(11), sm: r(13) },
                    }}
                  >
                    <option value="default">Default (Production)</option>
                    <option value="openai">OpenAI</option>
                    <option value="gemini">Gemini</option>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          
          <Tooltip title="Share this article">
            <IconButton 
              onClick={handleShareClick}
              aria-label="share article"
              aria-describedby={popoverId}
              sx={{ 
                color: 'primary.main',
                backgroundColor: 'rgba(46, 125, 50, 0.04)',
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: 'primary.light',
                  opacity: 0.8
                }
              }}
            >
              <ShareIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          <Popover
            id={popoverId}
            open={openPopover}
            anchorEl={popoverAnchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 3,
              sx: { 
                p: 2.5,
                borderRadius: '0.75rem',
                mt: 1,
                minWidth: { xs: 280, sm: 320 },
                border: '0.0625rem solid',
                borderColor: 'divider',
                background: 'background.paper',
              } 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ShareIcon sx={{ 
                mr: 1.5, 
                fontSize: '1.2rem', 
                color: 'primary.main'
              }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Share Research
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={window.location.href}
              InputProps={{
                readOnly: true,
                sx: { 
                  fontSize: '0.85rem',
                  backgroundColor: 'grey.100',
                  borderRadius: '0.5rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                     borderColor: 'grey.300',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                     borderColor: 'primary.main',
                     opacity: 0.5
                  },
                },
                endAdornment: (
                  <InputAdornment position="end" sx={{ pr: 0.5 }}>
                    <Tooltip title="Copy link">
                      <IconButton 
                        onClick={handleCopyLink} 
                        edge="end" 
                        size="small"
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { 
                            backgroundColor: 'primary.light',
                            opacity: 0.8
                          }
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              inputProps={{
                onClick: (e) => e.target.select()
              }}
            />
          </Popover>
        </Box>

        <ArticleHero
          article={article}
          supplementKey={supplementKey}
          conditionKey={conditionKey}
        />

        <Box
          sx={{
            background: color.paper,
            border: `0.0625rem solid`,
            borderColor: 'divider',
            borderRadius: '1rem',
            p: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
            mb: { xs: '1.5rem', sm: '2rem' },
          }}
        >
          {/* ── View Toggle (below shared sections, above tab content) ── */}
          {article.has_pdf_insights && (
            <Box sx={{ display: 'flex', gap: 0, mb: { xs: 2.5, sm: 3.5 }, borderBottom: `${r(1)} solid`, borderColor: 'divider', position: 'relative', zIndex: 1 }}>
              <Button
                disableRipple
                onClick={() => setViewMode('summary')}
                sx={{
                  px: 2, py: 1, minWidth: 0,
                  fontWeight: viewMode === 'summary' ? 600 : 400,
                  fontSize: '0.875rem',
                  color: viewMode === 'summary' ? 'text.primary' : 'text.secondary',
                  borderBottom: viewMode === 'summary' ? `${r(2)} solid` : `${r(2)} solid transparent`,
                  borderColor: viewMode === 'summary' ? 'primary.main' : 'transparent',
                  borderRadius: 0, textTransform: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { color: 'text.primary', backgroundColor: 'transparent' },
                }}
              >
                Summary
              </Button>
              <Button
                disableRipple
                onClick={() => setViewMode('analysis')}
                sx={{
                  px: 2, py: 1, minWidth: 0,
                  fontWeight: viewMode === 'analysis' ? 600 : 400,
                  fontSize: '0.875rem',
                  color: viewMode === 'analysis' ? 'text.primary' : 'text.secondary',
                  borderBottom: viewMode === 'analysis' ? `${r(2)} solid` : `${r(2)} solid transparent`,
                  borderColor: viewMode === 'analysis' ? 'primary.main' : 'transparent',
                  borderRadius: 0, textTransform: 'none',
                  transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  '&:hover': { color: 'text.primary', backgroundColor: 'transparent' },
                }}
              >
                <ScienceIcon sx={{ fontSize: '0.95rem' }} />
                Deep Dive
                {!hasAccess('pdf_insights') && (
                  <ProFeatureBadge label="Member" size="small" variant="accent" />
                )}
              </Button>
            </Box>
          )}

          {/* ── Tab Content ── */}
          <Box sx={{ pt: { xs: 1.25, sm: 1.75 } }}>
          {(() => {
            // Branch logic extracted to selectDeepDiveView (pure, unit-tested).
            // 'content' = member with data; 'limited' = member but pdf_insights
            // withheld by the B1 cap (graceful message, not a blank tab);
            // 'locked' = non-member upsell; 'summary' = the summary view below.
            // Computed once above, so the read-tracking gates cannot drift from
            // what is actually rendered here.
            const ddView = deepDiveView;
            if (ddView === 'content') return <Box ref={deepDiveRef}><ArticlePdfView article={article} /></Box>;
            if (ddView === 'limited') return <DeepDiveRateLimited />;
            if (ddView === 'locked') return <DeepAnalysisSection article={article} />;
            return (
          /* ── Summary View: Key Findings + Study Strength + Notable Effects ── */
          <Box ref={summaryRef}>
          {article.ai_abstract_summary && (
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'primary.main', mb: '0.25rem' }}>
                Summary
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.05rem' }, mb: '0.875rem', color: 'text.primary' }}>
                Key Findings
              </Typography>
              <Typography variant="body1" sx={{
                color: 'text.primary',
                lineHeight: 1.75,
                fontSize: { xs: '0.9rem', sm: '0.95rem' }
              }}>
                {article.ai_abstract_summary}
              </Typography>
            </Box>
          )}

          {article.ai_effects && article.ai_effects.length > 0 && (
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'primary.main', mb: '0.25rem' }}>
                Outcomes
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.05rem' }, mb: '0.875rem', color: 'text.primary' }}>
                Notable Effects
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {renderChips(article.ai_effects)}
              </Box>
            </Box>
          )}
          </Box>
            );
          })()}
          </Box>
        </Box>

        {viewMode !== 'analysis' && article.abstract && (
        <Accordion
          disableGutters
          elevation={1}
          sx={{
            mb: { xs: 3, sm: 4 },
            borderRadius: '0.5rem',
            backgroundColor: 'background.paper',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />}
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.25, sm: 1.5 },
              minHeight: 'unset',
              '& .MuiAccordionSummary-content': { my: 0 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                color: 'text.secondary',
              }}
            >
              Study Abstract
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 0, pb: { xs: 2, sm: 3, md: 4 } }}
          >
            <Box
              component="div"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(formatAbstractText(article.abstract))
              }}
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                color: 'text.secondary',
                lineHeight: 1.8,
                '& p': {
                  marginBottom: 2,
                  '&:last-child': { marginBottom: 0 }
                },
                '& strong, & b': {
                  color: 'text.primary',
                  fontWeight: 600,
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                  mt: 2.5,
                  mb: 1.25,
                  lineHeight: 1.5,
                  '&:first-of-type': { mt: 0 }
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 1.5,
                  '& li': {
                    fontSize: 'inherit',
                    mb: 0.75,
                    lineHeight: 1.7,
                  }
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                  '& th, & td': {
                    border: '0.0625rem solid',
                    borderColor: 'divider',
                    padding: { xs: '0.375rem 0.5rem', sm: '0.5rem 0.75rem' },
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    textAlign: 'left',
                    verticalAlign: 'top',
                  },
                  '& th': {
                    backgroundColor: 'grey.100',
                    fontWeight: 600,
                    color: 'text.primary',
                  }
                }
              }}
            />
          </AccordionDetails>
        </Accordion>
        )}

        {/* CTA — own box, below study abstract, before leaving */}
        {viewMode !== 'analysis' && (
          <ResearchStackCta
            supplementKey={supplementKey}
            conditionKey={conditionKey}
          />
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={1000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ 
            vertical: 'top', 
            horizontal: 'center' 
          }}
          sx={{
            zIndex: (theme) => theme.zIndex.snackbar,
            top: { xs: 72, sm: 24 }, 
            width: { xs: 'calc(100% - 2rem)', sm: 'auto' },
            left: { xs: '1rem', sm: '50%' },
            right: { xs: '1rem', sm: 'auto' },
            transform: { xs: 'none', sm: 'translateX(-50%)' }, 
          }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="success" 
            elevation={6}
            variant="standard"
            sx={{ 
              width: '100%',
              fontWeight: 500,
              fontSize: { xs: '0.9rem', sm: '0.875rem' }, 
              py: 1, 
              px: '0.75rem',
            }}
          >
            Link copied to clipboard!
          </Alert>
        </Snackbar>

      </Box>
    </>
  );
};

export default ArticleDetail;